import { create } from 'zustand';
import { jwtDecode } from 'jwt-decode';
import { baseUrl, postRequest, patchRequest } from '../utils/services';
import { useSessionStore } from './useSessionStore';

export const useAuthStore = create((set, get) => ({
  user: null,
  registerError: null,
  isLoginLoading: false,
  isRegisterLoading: false,
  registerInfo: {},
  needsPasswordReset: false,
  passwordChangeMessage: '',
  loginInfo: {
    email: '',
    password: '',
  },

  // Setters
  setUser: (userOrFn) => set((state) => ({
    user: typeof userOrFn === 'function' ? userOrFn(state.user) : userOrFn,
  })),
  setRegisterError: (error) => set({ registerError: error }),
  setIsLoginLoading: (loading) => set({ isLoginLoading: loading }),
  setIsRegisterLoading: (loading) => set({ isRegisterLoading: loading }),
  setRegisterInfo: (info) => set({ registerInfo: info }),
  setNeedsPasswordReset: (needs) => set({ needsPasswordReset: needs }),
  setPasswordChangeMessage: (message) => set({ passwordChangeMessage: message }),
  setLoginInfo: (info) => set({ loginInfo: info }),

  updateRegisterInfo: (info) => set({ registerInfo: info }),
  updateLoginInfo: (info) => set({ loginInfo: info }),
  updateOnlineStatus: async () => {
    const { user } = get();

    if (!user?.id) {
      return;
    }

    await patchRequest(`${baseUrl}/users/online`, {});
  },

  // Actions
  initializeAuth: async () => {
    const storedUser = localStorage.getItem('User');
    if (storedUser) {
      set({ user: JSON.parse(storedUser) });
    }

    const tempSession = localStorage.getItem('tempSession');
    const accessToken = localStorage.getItem('accessToken');

    if (tempSession && accessToken) {
      sessionStorage.setItem('User', tempSession);
      localStorage.removeItem('tempSession');
      set({ user: JSON.parse(tempSession) });

      const decoded = jwtDecode(accessToken);
      if (decoded.exp * 1000 < Date.now()) {
        get().logoutUser();
      }
    }
  },

  registerUser: async (e, navigate) => {
    e.preventDefault();
    const { registerInfo } = get();

    set({ isRegisterLoading: true, registerError: null });

    const response = await postRequest(`${baseUrl}/users/register`, registerInfo);

    set({ isRegisterLoading: false });

    if (response.error) {
      return set({ registerError: response });
    }

    localStorage.setItem('User', JSON.stringify(response));
    set({ user: null });
    if (navigate) navigate('/');
  },

  loginUser: async (e, recaptchaToken, captchaRef, setErrorMessage) => {
    if (e) e.preventDefault();

    const { loginInfo } = get();

    if (!recaptchaToken && process.env.NODE_ENV !== 'development') {
      set({
        loginInfo: { email: '', password: '' },
      });
      return setErrorMessage('Por favor, complete o reCAPTCHA para prosseguir.');
    }

    set({ isLoginLoading: true });

    try {
      const response = await postRequest(`${baseUrl}/users/login`, {
        email: loginInfo.email.trim(),
        password: loginInfo.password.trim(),
        recaptchaToken,
      });

      set({ isLoginLoading: false });

      if (response.error) {
        set({
          loginInfo: { email: '', password: '' },
        });

        if (captchaRef?.current) {
          captchaRef.current.reset();
        }

        return setErrorMessage(response.message);
      }

      const { accessToken, refreshToken, ...userData } = response;

      if (response.needsPasswordReset) {
        useSessionStore.getState().resetSessionInvalidation();
        set({ user: response, needsPasswordReset: true });
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('User', JSON.stringify(userData));
        return;
      }

      useSessionStore.getState().resetSessionInvalidation();
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('User', JSON.stringify(userData));
      set({ user: userData });
    } catch (error) {
      set({ isLoginLoading: false });
      setErrorMessage(error.message || 'Erro ao fazer login');
    }
  },

  logoutUser: async () => {
    try {
      await postRequest(`${baseUrl}/users/logout`, {});
    } finally {
      localStorage.removeItem('User');
      localStorage.removeItem('tempSession');
      localStorage.removeItem('accessToken');
      set({
        user: null,
        loginInfo: { email: '', password: '' },
      });
    }
  },
}));
